import { createFileRoute, Navigate } from '@tanstack/react-router';

import { useAbility } from '@features/authorization';
import {
  CustomerDocumentFullPageAccessGate,
  CustomerDocumentProfile,
  normalizeDocumentProfileSearch,
  projectCrmCustomerDocumentsListSearch,
  type CrmCustomerDocumentsListSearch,
  type DocumentProfileSearch,
} from '@features/crm-customer-documents';
import { canAccessCrmCustomerDocuments } from '@shared/lib';
import { firstAccessibleCrmCustomerRecordArea } from '@widgets/crm-customer-record-shell';

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/documents/$documentId'
)({
  // TanStack provides an untyped search record here; the normalizer validates
  // it and maps the retired Audit state to the canonical General state.

  validateSearch: (search): DocumentProfileSearch => normalizeDocumentProfileSearch(search),
  component: CustomerDocumentProfileRoute,
});

function CustomerDocumentProfileRoute() {
  const navigate = Route.useNavigate();
  const { userProfileId: customerId, documentId, locale } = Route.useParams();
  // The route normalizer above is the runtime validation boundary.

  const search = Route.useSearch() as unknown as DocumentProfileSearch;
  // Route validation above ensures this narrow use of the typed search value.

  const activeTab = search.documentTab === 'file' ? 'file' : 'general';
  const ability = useAbility();
  if (!canAccessCrmCustomerDocuments(ability))
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CustomerDocumentFullPageAccessGate
        customerId={customerId}
        onAccessUnavailable={(allowedAreas) => {
          const fallback = firstAccessibleCrmCustomerRecordArea(allowedAreas);
          return (
            <Navigate
              to={fallback?.route ?? '/$locale/unauthorized'}
              params={{ userProfileId: customerId, locale }}
              replace
            />
          );
        }}
      >
        {(viewerAccessScope) => (
          <CustomerDocumentProfile
            activeTab={activeTab}
            customerId={customerId}
            documentId={documentId}
            viewerAccessScope={viewerAccessScope}
            backToMembershipDocuments={search.membershipReturnTo !== undefined}
            initialEdit={search.edit === true}
            onEditEntryConsumed={() => {
              void navigate({
                replace: true,
                search: (current) => ({ ...current, edit: undefined }),
              });
            }}
            onBack={async () => {
              if (search.membershipReturnTo) {
                await navigate({ to: search.membershipReturnTo as never });
                return;
              }
              if (search.learningRegistrationId) {
                await navigate({
                  to: '/$locale/crm/user-profiles/$userProfileId/learning/registrations/$registrationId',
                  params: {
                    userProfileId: customerId,
                    locale,
                    registrationId: search.learningRegistrationId,
                  },
                  search: { registrationProfileTab: 'documents' },
                });
                return;
              }
              await navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/documents',
                params: { userProfileId: customerId, locale },
                search: (current: Record<string, unknown>) => {
                  const customerRecordSearch = { ...current };
                  delete customerRecordSearch.documentTab;
                  delete customerRecordSearch.edit;
                  return {
                    ...customerRecordSearch,
                    ...projectCrmCustomerDocumentsListSearch(
                      current as unknown as Partial<CrmCustomerDocumentsListSearch>
                    ),
                  };
                },
              });
            }}
            onTabChange={(tab) => {
              void navigate({
                replace: true,
                search: (current: Record<string, unknown>) => ({
                  ...current,
                  ...projectCrmCustomerDocumentsListSearch(
                    current as unknown as Partial<CrmCustomerDocumentsListSearch>
                  ),
                  documentTab: tab === 'general' ? undefined : tab,
                  learningRegistrationId: search.learningRegistrationId,
                  membershipReturnTo: search.membershipReturnTo,
                }),
              });
            }}
          />
        )}
      </CustomerDocumentFullPageAccessGate>
    </main>
  );
}
